import type { AuthUser } from '@/utils/sessionStorage'

/** Row shape needed to check visibility (account_contacts.attribution_id) */
export type ContactAttribution = {
  attribution_id: string | null
}

export function isAgencyUser(user: AuthUser | null): boolean {
  return user?.type === 'agency_user'
}

/** When false, full contact list queries need no extra attribution filter */
export function shouldApplyContactScope(user: AuthUser | null): boolean {
  if (!user) return false
  if (isAgencyUser(user)) return false
  if (user.view_all_contacts === true) return false
  return true
}

export function contactMatchesScope(user: AuthUser | null, contact: ContactAttribution): boolean {
  if (!shouldApplyContactScope(user)) return true
  const authId = user!.auth_user_id
  if (!authId) return false

  const aid = contact.attribution_id ?? null

  if (user!.view_unassigned_contacts === true) {
    return aid === null || aid === authId
  }
  return aid === authId
}

/** Restringe linhas de account_contacts ao escopo do membro (Supabase query builder). */
export function applyChatContactScopeFilter(query: any, user: AuthUser | null): any {
  if (!shouldApplyContactScope(user)) return query
  const authId = user!.auth_user_id
  if (!authId) {
    return query.eq('attribution_id', '00000000-0000-0000-0000-000000000001')
  }
  if (user!.view_unassigned_contacts === true) {
    return query.or(`attribution_id.is.null,attribution_id.eq.${authId}`)
  }
  return query.eq('attribution_id', authId)
}

/**
 * Restringe linhas de uma tabela relacionada ao escopo do membro através de
 * um relacionamento com account_contacts (ex.: account_opportunities -> contact).
 *
 * IMPORTANTE: o caller deve declarar o relacionamento como inner join no
 * `select`, ex.: `contact:account_contacts!inner(...)`. Caso contrário linhas
 * sem contato relacionado serão retornadas mesmo fora do escopo.
 *
 * @param relation Nome do alias usado no select (default: 'contact').
 */
export function applyContactScopeFilterViaRelation(
  query: any,
  user: AuthUser | null,
  relation: string = 'contact'
): any {
  if (!shouldApplyContactScope(user)) return query
  const authId = user!.auth_user_id
  if (!authId) {
    return query.eq(`${relation}.attribution_id`, '00000000-0000-0000-0000-000000000001')
  }
  if (user!.view_unassigned_contacts === true) {
    return query.or(
      `attribution_id.is.null,attribution_id.eq.${authId}`,
      { foreignTable: relation }
    )
  }
  return query.eq(`${relation}.attribution_id`, authId)
}

/**
 * Confere se um contato (por id) está dentro do escopo de visibilidade do usuário.
 * Usar antes de mutações que dependem de propriedade do contato (oportunidades, etc).
 */
export async function assertContactInScope(
  client: any,
  contactId: string,
  accountId: string,
  user: AuthUser | null
): Promise<boolean> {
  if (!shouldApplyContactScope(user)) return true
  if (!client || !contactId || !accountId) return false

  const { data, error } = await client
    .from('account_contacts')
    .select('attribution_id')
    .eq('id', contactId)
    .eq('account_id', accountId)
    .maybeSingle()

  if (error || !data) return false
  return contactMatchesScope(user, { attribution_id: data.attribution_id ?? null })
}

/**
 * Confere se uma oportunidade (por id) tem contato dentro do escopo de visibilidade do usuário.
 */
export async function assertOpportunityInScope(
  client: any,
  opportunityId: string,
  accountId: string,
  user: AuthUser | null
): Promise<boolean> {
  if (!shouldApplyContactScope(user)) return true
  if (!client || !opportunityId || !accountId) return false

  const { data, error } = await client
    .from('account_opportunities')
    .select('contact_id, contact:account_contacts(attribution_id)')
    .eq('id', opportunityId)
    .eq('account_id', accountId)
    .maybeSingle()

  if (error || !data) return false
  const attribution = (data as any)?.contact?.attribution_id ?? null
  return contactMatchesScope(user, { attribution_id: attribution })
}

/** Decide qual valor de attribution_id usar quando o membro NÃO pode atribuir a outros. */
export function clampAttributionIdForSave(
  attributionId: string | null,
  user: AuthUser | null
): string | null {
  if (!user || isAgencyUser(user) || user.can_assign_contacts === true) {
    return attributionId
  }
  const self = user.auth_user_id ?? null
  if (!self) return attributionId

  if (!attributionId) {
    return self
  }
  if (attributionId === self) {
    return self
  }
  return self
}

export function canSeeChatOwnershipTabUnassigned(user: AuthUser | null): boolean {
  if (!user) return false
  if (isAgencyUser(user)) return true
  if (user.view_all_contacts === true) return true
  return user.view_unassigned_contacts === true
}

/** Clamp attribution filter to what the user is allowed to use in the chat UI */
/** Pode o filtro "Usuário atribuído" restringir a lista a outro membro (além de si)? */
export function canFilterChatByOtherMembers(user: AuthUser | null): boolean {
  if (!user) return false
  if (isAgencyUser(user)) return true
  return user.view_all_contacts === true
}

export function clampChatAttributionId(
  attributionId: string | null,
  user: AuthUser | null
): string | null {
  if (!user || isAgencyUser(user) || user.view_all_contacts === true) {
    return attributionId
  }
  const self = user.auth_user_id
  if (!self) return attributionId

  if (user.view_unassigned_contacts === true) {
    if (!attributionId || attributionId === '__unassigned__' || attributionId === self) {
      return attributionId
    }
    return null
  }

  if (attributionId === '__unassigned__') {
    return self
  }
  if (attributionId && attributionId !== self) {
    return self
  }
  return attributionId
}
